import apiClient, { buildQueryParams } from '../utils/apiClient';
import { handleError } from '../utils/errorHandler';
import { API_ENDPOINTS } from '../constants/apiEndpoints.constants';

/**
 * Service for Booking API calls
 */
const bookingService = {
  /**
   * Get all bookings with pagination and filters
   */
  getAllBookings: async (params = {}) => {
    try {
      const {
        page = 1,
        limit = 20,
        status,
        clientId,
        vendorId,
        vehicleId,
        startDate,
        endDate,
        paymentStatus,
        search,
        sortBy = 'createdAt',
        sortOrder = 'desc',
      } = params;

      const queryParams = buildQueryParams({
        page,
        limit,
        status,
        clientId,
        vendorId,
        vehicleId,
        startDate,
        endDate,
        paymentStatus,
        search,
        sortBy,
        sortOrder,
      });

      return await apiClient.get(`${API_ENDPOINTS.BOOKINGS.LIST}?${queryParams}`);
    } catch (error) {
      throw new Error(handleError(error, 'bookingService.getAllBookings'));
    }
  },

  /**
   * Get booking by ID
   */
  getBookingById: async (id) => {
    try {
      return await apiClient.get(API_ENDPOINTS.BOOKINGS.GET_BY_ID(id));
    } catch (error) {
      throw new Error(handleError(error, 'bookingService.getBookingById'));
    }
  },

  /**
   * Update booking status
   */
  updateBookingStatus: async (id, status, reason) => {
    try {
      return await apiClient.patch(API_ENDPOINTS.BOOKINGS.UPDATE_STATUS(id), { status, reason });
    } catch (error) {
      throw new Error(handleError(error, 'bookingService.updateBookingStatus'));
    }
  },

  /**
   * Update payment status
   */
  updatePaymentStatus: async (id, paymentStatus) => {
    try {
      return await apiClient.patch(API_ENDPOINTS.BOOKINGS.UPDATE_PAYMENT_STATUS(id), { paymentStatus });
    } catch (error) {
      throw new Error(handleError(error, 'bookingService.updatePaymentStatus'));
    }
  },

  /**
   * Update booking dates
   */
  updateBookingDates: async (id, startDate, endDate) => {
    try {
      return await apiClient.patch(API_ENDPOINTS.BOOKINGS.UPDATE_DATES(id), { startDate, endDate });
    } catch (error) {
      throw new Error(handleError(error, 'bookingService.updateBookingDates'));
    }
  },

  /**
   * Update booking financials
   */
  updateBookingFinancials: async (id, financials) => {
    try {
      return await apiClient.patch(API_ENDPOINTS.BOOKINGS.GET_FINANCIALS(id), financials);
    } catch (error) {
      throw new Error(handleError(error, 'bookingService.updateBookingFinancials'));
    }
  },

  /**
   * Regenerate OTP codes
   */
  regenerateOTP: async (id, type = 'both') => {
    try {
      return await apiClient.post(API_ENDPOINTS.BOOKINGS.REGENERATE_OTP(id), { type });
    } catch (error) {
      throw new Error(handleError(error, 'bookingService.regenerateOTP'));
    }
  },

  /**
   * Get booking statistics
   */
  getBookingStats: async (startDate, endDate) => {
    try {
      const queryParams = buildQueryParams({
        startDate,
        endDate,
      });

      return await apiClient.get(`${API_ENDPOINTS.BOOKINGS.STATS}?${queryParams}`);
    } catch (error) {
      throw new Error(handleError(error, 'bookingService.getBookingStats'));
    }
  },

  /**
   * Delete booking
   */
  deleteBooking: async (id) => {
    try {
      return await apiClient.delete(API_ENDPOINTS.BOOKINGS.DELETE(id));
    } catch (error) {
      throw new Error(handleError(error, 'bookingService.deleteBooking'));
    }
  },

  /**
   * Create a new booking
   */
  createBooking: async (bookingData) => {
    try {
      return await apiClient.post(API_ENDPOINTS.USER_BOOKINGS.CREATE, bookingData);
    } catch (error) {
      throw new Error(handleError(error, 'bookingService.createBooking'));
    }
  },

  /**
   * Bulk update bookings
   */
  bulkUpdateBookings: async (bookingIds, action, value) => {
    try {
      return await apiClient.patch(API_ENDPOINTS.BOOKINGS.BULK, { bookingIds, action, value });
    } catch (error) {
      throw new Error(handleError(error, 'bookingService.bulkUpdateBookings'));
    }
  },

  /**
   * Get vendor bookings
   * Appelle GET /api/bookings/vendor avec les paramètres de pagination et tri
   * Le Bearer Token est automatiquement ajouté par apiClient pour identifier req.user.id
   */
  getVendorBookings: async (params = {}) => {
    try {
      const {
        page = 1,
        limit = 20,
        status,
        sortBy = 'createdAt',
        sortOrder = 'desc',
      } = params;

      const queryParams = buildQueryParams({
        page,
        limit,
        status,
        sortBy,
        sortOrder,
      });

      // L'endpoint est /api/bookings/vendor
      // Le token Bearer est automatiquement ajouté par l'intercepteur de apiClient
      return await apiClient.get(`${API_ENDPOINTS.VENDOR_BOOKINGS.LIST}?${queryParams}`);
    } catch (error) {
      throw new Error(handleError(error, 'bookingService.getVendorBookings'));
    }
  },
};

export default bookingService;
